import React from 'react';

import { createBrowserRouter } from 'react-router-dom';
import { Navigate } from 'react-router-dom';

import BudgetsPage from '@/pages/Budgets';
import BudgetAddPage from '@/pages/Budgets/Add';
import BudgetDetailPage from '@/pages/Budgets/Detail';
import ForgotPasswordPage from '@/pages/ForgotPassword';
import LandingPage from '@/pages/Landing';
import LoginPage from '@/pages/Login';
import OnboardingLayout from '@/pages/Onboarding/OnboardingLayout';
import OverviewPage from '@/pages/Onboarding/Overview';
import OnboardingProfilePage from '@/pages/Onboarding/Profile';
import SubscriptionPage from '@/pages/Onboarding/Subscription';
import PrivacyPolicyPage from '@/pages/PrivacyPolicy';
import ResetPasswordPage from '@/pages/ResetPassword';
import SignUpPage from '@/pages/SignUp';
import TermsPage from '@/pages/Terms';
import PublicRoute from './PublicRoute';
import PrivateRoute from './PrivateRoute';
import NotFoundPage from '@/pages/NotFound';
import ProfilePage from '@/pages/Profile';

import { Pages } from './types';
import DashboardPage from '@/pages/Dashboard';
import CommitmentPage from '@/pages/Commitment';
import InvoicePage from '@/pages/Invoice';
import ProfileLayout from '@/pages/Profile/ProfileLayout';
import EditProfilePage from '@/pages/Profile/Edit/EditProfilePage';
import ProfileSubscriptionPage from '@/pages/Profile/Subscription/SubscriptionPage';
import BillingHistoryPage from '@/pages/Profile/BillingHistory/BillingHistoryPage';
import TerminologyPage from '@/pages/Profile/Terminology/TerminologyPage';
import PaymentMethodPage from '@/pages/Profile/PaymentMethod/PaymentMethodPage';
import BankAccountPage from '@/pages/BankAccount';
import BankAccountAddPage from '@/pages/BankAccount/Add';
import EditPage from '@/pages/Budgets/Edit/EditBudgetPage';
import BankAccountOutlet from '@/pages/BankAccount/BankAccountOutlet';
import BankAccountDetailPage from '@/pages/BankAccount/Detail/BankAccountDetailPage';
import BankAccountEditPage from '@/pages/BankAccount/Edit/BankAccountEditPage';
import AccountingCenterOutlet from '@/pages/AccountingCenter';
import AccountingCentersPage from '@/pages/AccountingCenter/AccountingCentersPage';
import ACAddPage from '@/pages/AccountingCenter/Add/ACAddPage';
import ACEditPage from '@/pages/AccountingCenter/Edit/ACEditPage';
import ACDetailPage from '@/pages/AccountingCenter/Detail/ACDetailPage';
import ACAdjustPage from '@/pages/AccountingCenter/Adjust/ACAdjust';
import ExpenseCategoryOutlet from '@/pages/ExpenseCategory';
import ExpenseCategoryPage from '@/pages/ExpenseCategory/ExpenseCategoryPage';
import ECAddPage from '@/pages/ExpenseCategory/Add/ECAddPage';
import ECDetailPage from '@/pages/ExpenseCategory/Detail/ECDetailPage';
import ECEditPage from '@/pages/ExpenseCategory/Edit/ECEditPage';
import ECAdjustPage from '@/pages/ExpenseCategory/Adjust/ECAdjustPage';
import RevenueCategoryOutlet from '@/pages/RevenueCategory';
import RevenueCategoryPage from '@/pages/RevenueCategory/RevenueCategoryPage';
import RCAddPage from '@/pages/RevenueCategory/Add/RCAddPage';
import RCDetailPage from '@/pages/RevenueCategory/Detail/RCDetailPage';
import RCEditPage from '@/pages/RevenueCategory/Edit/RCEditPage';
import RCAdjustPage from '@/pages/RevenueCategory/Adjust/RCAdjustPage';
import AdminRoute from '@/routes/AdminRoute';
import AdminDashboardPage from '@/pages/AdminDashboardPage';
import AdminSubscribersPage from '@/pages/AdminSubscriberPage';
import SubscriberPage from '@/pages/AdminSubscriberPage/Subscriber/SubscriberPage';
import InvoiceCreatePage from '@/pages/Invoice/InvoiceCreate/InvoiceCreatePage';
import InvoiceOutlet from '@/pages/Invoice/InvoiceOutlet';
import InvoiceDetailPage from '@/pages/Invoice/InvoiceDetail/InvoiceDetailPage';
import BudgetInvoiceDetailPage from '@/pages/Budgets/InvoiceDetail/InvoiceDetail';
import VerifyEmailPage from '@/pages/VerifyEmail';
import WorkActivityCreatePage from '@/pages/WorkActivity/Create/WorkActivityCreatePage';
import WorkActivityPage from '@/pages/WorkActivity/WorkActivityPage';
import WorkActivityDetail from '@/pages/WorkActivity/Detail/WorkActivityDetail';
import WorkActivitySubmitRevision from '@/pages/WorkActivity/SubmitRevision/WorkActivitySubmitRevision';
import WorkActivityEditPage from '@/pages/WorkActivity/Edit/WorkActivityEditPage';
import MembersEditPage from '@/pages/Budgets/Members/Edit/MembersEditPage';
import MembersPage from '@/pages/Budgets/Members/MembersPage';
import NewLandingPage from '@/pages/Landing/NewLandingPage';

export const PAGE: Pages = {
  LANDING: {
    path: '/',
    navTitle: 'Landing'
  },
  WELCOME: {
    path: '/welcome',
    navTitle: 'Welcome'
  },
  LOGIN: {
    path: '/login',
    navTitle: 'Login'
  },
  SIGNUP: {
    path: '/signup',
    navTitle: 'SignUp'
  },
  VERIFY_EMAIL: {
    path: '/verify-email',
    navTitle: 'Verify Email'
  },
  ONBOARDING: {
    path: '/onboarding',
    navTitle: 'Onboarding',
    children: {
      PROFILE: 'profile',
      OVERVIEW: 'overview',
      SUBSCRIPTION: 'subscription'
    }
  },
  TERMS: {
    path: '/terms',
    navTitle: 'Terms'
  },
  PRIVACY_POLICY: {
    path: '/privacy-policy',
    navTitle: 'PrivacyPolicy'
  },
  FORGOT_PASSWORD: {
    path: '/forgot-password',
    navTitle: 'ForgotPassword'
  },
  RESET_PASSWORD: {
    path: '/reset-password',
    navTitle: 'ResetPassword'
  },
  BUDGETS: {
    path: '/budgets',
    navTitle: 'Budgets',
    children: {
      ADD: 'add',
      DETAIL: ':budgetId',
      EDIT: 'edit/:budgetId',
      INVOICES: ':budgetId/invoices',
      INVOICE_DETAIL: ':budgetId/invoices/:invoiceId',
      MEMBERS: ':budgetId/members',
      MEMBERS_EDIT: ':budgetId/members/edit',
      WORK_ACTIVITY: ':budgetId/work-activity',
      WORK_ACTIVITY_DETAIL: ':budgetId/work-activity/:workActivityId',
      WORK_ACTIVITY_CREATE: ':budgetId/work-activity/create',
      WORK_ACTIVITY_SUBMIT_REVISION: ':budgetId/work-activity/:workActivityId/submit-revision',
      WORK_ACTIVITY_EDIT: ':budgetId/work-activity/:workActivityId/edit'
    }
  },
  BANK_ACCOUNTS: {
    path: '/budgets/:budgetId/bank-accounts',
    navTitle: 'Bank Accounts',
    children: {
      ADD: 'add',
      DETAIL: ':bankAccountId',
      EDIT: ':bankAccountId/edit'
    }
  },
  DASHBOARD: {
    path: '/dashboard',
    navTitle: 'Dashboard'
  },
  PROFILE: {
    path: '/profile',
    navTitle: 'Profile',
    children: {
      EDIT: 'edit',
      SUBSCRIPTION: 'subscription',
      BILLING_HISTORY: 'billing-history',
      TERMINOLOGY: 'terminology',
      PAYMENT_METHOD: 'payment-method'
    }
  },
  COMMITMENT: {
    path: '/commitment',
    navTitle: 'Commitment'
  },
  INVOICE: {
    path: '/invoice',
    navTitle: 'Invoice',
    children: {
      CREATE: 'create',
      DETAIL: ':invoiceId',
      EDIT: ':invoiceId/edit'
    }
  },
  ACCOUNTING_CENTER: {
    path: '/budgets/:budgetId/accounting-centers/:acType',
    navTitle: 'Accounting Centers',
    children: {
      ADD: 'add',
      EDIT: 'edit/:acId',
      DETAIL: ':acId',
      ADJUST: 'adjust'
    }
  },
  EXPENSE_CATEGORY: {
    path: '/budgets/:budgetId/expense-categories',
    navTitle: 'Expense Categories',
    children: {
      ADD: 'add',
      EDIT: 'edit/:ecId',
      DETAIL: ':ecId',
      ADJUST: 'adjust'
    }
  },
  REVENUE_CATEGORY: {
    path: '/budgets/:budgetId/revenue-categories',
    navTitle: 'Revenue Categories',
    children: {
      ADD: 'add',
      EDIT: 'edit/:rcId',
      DETAIL: ':rcId',
      ADJUST: 'adjust'
    }
  },
  ADMIN_DASHBOARD: {
    path: '/admin/dashboard',
    navTitle: 'Admin Dashboard'
  },
  ADMIN_SUBSCRIBERS: {
    path: '/admin/subscribers',
    navTitle: 'Admin Subscribers'
  },
  ADMIN_PROFILE: {
    path: '/admin/profile',
    navTitle: 'Admin Profile'
  }
} as const;

export const routes = createBrowserRouter([
  {
    path: PAGE.LANDING.path,
    element: React.createElement(PublicRoute, {
      children: React.createElement(NewLandingPage) as React.ReactElement,
      redirectTo: '/dashboard'
    }) as React.ReactElement
  },
  {
    path: PAGE.WELCOME.path,
    element: React.createElement(PublicRoute, {
      children: React.createElement(LandingPage) as React.ReactElement,
      redirectTo: '/dashboard'
    }) as React.ReactElement
  },
  {
    path: PAGE.LOGIN.path,
    element: React.createElement(PublicRoute, {
      children: React.createElement(LoginPage) as React.ReactElement,
      redirectTo: '/dashboard'
    }) as React.ReactElement
  },
  {
    path: PAGE.SIGNUP.path,
    element: React.createElement(PublicRoute, {
      children: React.createElement(SignUpPage) as React.ReactElement,
      redirectTo: '/dashboard'
    }) as React.ReactElement
  },
  {
    path: PAGE.VERIFY_EMAIL.path,
    element: React.createElement(PublicRoute, {
      children: React.createElement(VerifyEmailPage) as React.ReactElement,
      redirectTo: '/dashboard'
    }) as React.ReactElement
  },
  {
    path: PAGE.ONBOARDING.path,
    element: React.createElement(PrivateRoute, {
      children: React.createElement(OnboardingLayout) as React.ReactElement,
      redirectTo: '/login'
    }) as React.ReactElement,
    children: [
      {
        index: true,
        element: React.createElement(Navigate, {
          to: `${PAGE.ONBOARDING.path}/${PAGE.ONBOARDING.children.PROFILE}`,
          replace: true
        }) as React.ReactElement
      },
      {
        path: PAGE.ONBOARDING.children.PROFILE,
        element: React.createElement(OnboardingProfilePage) as React.ReactElement
      },
      {
        path: PAGE.ONBOARDING.children.OVERVIEW,
        element: React.createElement(OverviewPage) as React.ReactElement
      },
      {
        path: PAGE.ONBOARDING.children.SUBSCRIPTION,
        element: React.createElement(SubscriptionPage) as React.ReactElement
      }
    ]
  },
  // Public routes without auth check
  {
    path: PAGE.TERMS.path,
    element: React.createElement(TermsPage) as React.ReactElement
  },
  {
    path: PAGE.PRIVACY_POLICY.path,
    element: React.createElement(PrivacyPolicyPage) as React.ReactElement
  },
  {
    path: PAGE.FORGOT_PASSWORD.path,
    element: React.createElement(PublicRoute, {
      children: React.createElement(ForgotPasswordPage) as React.ReactElement,
      redirectTo: '/dashboard'
    }) as React.ReactElement
  },
  {
    path: PAGE.RESET_PASSWORD.path,
    element: React.createElement(PublicRoute, {
      children: React.createElement(ResetPasswordPage) as React.ReactElement,
      redirectTo: '/dashboard'
    }) as React.ReactElement
  },
  {
    path: PAGE.BUDGETS.path,
    element: React.createElement(PrivateRoute, {
      children: React.createElement(BudgetsPage) as React.ReactElement,
      redirectTo: '/login'
    }) as React.ReactElement,
    children: [
      {
        path: PAGE.BUDGETS.children.ADD,
        element: React.createElement(BudgetAddPage) as React.ReactElement
      },
      {
        path: PAGE.BUDGETS.children.DETAIL,
        element: React.createElement(BudgetDetailPage) as React.ReactElement
      },
      {
        path: PAGE.BUDGETS.children.EDIT,
        element: React.createElement(EditPage) as React.ReactElement
      },
      {
        path: PAGE.BUDGETS.children.CREATE_INVOICE,
        element: React.createElement(InvoiceCreatePage) as React.ReactElement
      },
      {
        path: PAGE.BUDGETS.children.INVOICES,
        element: React.createElement(InvoicePage) as React.ReactElement
      },
      {
        path: PAGE.BUDGETS.children.INVOICE_DETAIL,
        element: React.createElement(BudgetInvoiceDetailPage) as React.ReactElement
      },
      {
        path: PAGE.BUDGETS.children.WORK_ACTIVITY_CREATE,
        element: React.createElement(WorkActivityCreatePage) as React.ReactElement
      },
      {
        path: PAGE.BUDGETS.children.WORK_ACTIVITY_SUBMIT_REVISION,
        element: React.createElement(WorkActivitySubmitRevision) as React.ReactElement
      },
      {
        path: PAGE.BUDGETS.children.WORK_ACTIVITY_EDIT,
        element: React.createElement(WorkActivityEditPage) as React.ReactElement
      },
      {
        path: PAGE.BUDGETS.children.WORK_ACTIVITY_DETAIL,
        element: React.createElement(WorkActivityDetail) as React.ReactElement
      },
      {
        path: PAGE.BUDGETS.children.WORK_ACTIVITY,
        element: React.createElement(WorkActivityPage) as React.ReactElement
      },
      {
        path: PAGE.BUDGETS.children.MEMBERS,
        element: React.createElement(MembersPage) as React.ReactElement
      },
      {
        path: PAGE.BUDGETS.children.MEMBERS_EDIT,
        element: React.createElement(MembersEditPage) as React.ReactElement
      }
    ]
  },
  // Bank accounts routes
  {
    path: PAGE.BANK_ACCOUNTS.path,
    element: React.createElement(PrivateRoute, {
      children: React.createElement(BankAccountOutlet) as React.ReactElement,
      redirectTo: '/login'
    }) as React.ReactElement,
    children: [
      {
        index: true,
        element: React.createElement(BankAccountPage) as React.ReactElement
      },
      {
        path: PAGE.BANK_ACCOUNTS.children.DETAIL,
        element: React.createElement(BankAccountDetailPage) as React.ReactElement
      },
      {
        path: PAGE.BANK_ACCOUNTS.children.EDIT,
        element: React.createElement(BankAccountEditPage) as React.ReactElement
      },
      {
        path: PAGE.BANK_ACCOUNTS.children.ADD,
        element: React.createElement(BankAccountAddPage) as React.ReactElement
      }
    ]
  },
  {
    path: PAGE.DASHBOARD.path,
    element: React.createElement(PrivateRoute, {
      children: React.createElement(DashboardPage) as React.ReactElement,
      redirectTo: '/login'
    }) as React.ReactElement
  },
  {
    path: PAGE.PROFILE.path,
    element: React.createElement(PrivateRoute, {
      children: React.createElement(ProfileLayout) as React.ReactElement,
      redirectTo: '/login'
    }) as React.ReactElement,
    children: [
      {
        index: true,
        element: React.createElement(ProfilePage) as React.ReactElement
      },
      {
        path: PAGE.PROFILE.children.EDIT,
        element: React.createElement(EditProfilePage) as React.ReactElement
      },
      {
        path: PAGE.PROFILE.children.SUBSCRIPTION,
        element: React.createElement(ProfileSubscriptionPage) as React.ReactElement
      },
      {
        path: PAGE.PROFILE.children.BILLING_HISTORY,
        element: React.createElement(BillingHistoryPage) as React.ReactElement
      },
      {
        path: PAGE.PROFILE.children.TERMINOLOGY,
        element: React.createElement(TerminologyPage) as React.ReactElement
      },
      {
        path: PAGE.PROFILE.children.PAYMENT_METHOD,
        element: React.createElement(PaymentMethodPage) as React.ReactElement
      }
    ]
  },
  {
    path: PAGE.COMMITMENT.path,
    element: React.createElement(PrivateRoute, {
      children: React.createElement(CommitmentPage) as React.ReactElement,
      redirectTo: '/login'
    }) as React.ReactElement
  },
  {
    path: PAGE.INVOICE.path,
    element: React.createElement(PrivateRoute, {
      children: React.createElement(InvoiceOutlet) as React.ReactElement,
      redirectTo: '/login'
    }) as React.ReactElement,
    children: [
      {
        index: true,
        element: React.createElement(InvoicePage) as React.ReactElement
      },
      {
        path: PAGE.INVOICE.children.CREATE,
        element: React.createElement(InvoiceCreatePage) as React.ReactElement
      },
      {
        path: PAGE.INVOICE.children.DETAIL,
        element: React.createElement(InvoiceDetailPage) as React.ReactElement
      },
      {
        path: PAGE.INVOICE.children.EDIT,
        element: React.createElement(InvoiceCreatePage) as React.ReactElement
      }
    ]
  },
  // Accounting center routes
  {
    path: PAGE.ACCOUNTING_CENTER.path,
    element: React.createElement(PrivateRoute, {
      children: React.createElement(AccountingCenterOutlet) as React.ReactElement,
      redirectTo: '/login'
    }) as React.ReactElement,
    children: [
      {
        index: true,
        element: React.createElement(AccountingCentersPage) as React.ReactElement
      },
      {
        path: PAGE.ACCOUNTING_CENTER.children.ADD,
        element: React.createElement(ACAddPage) as React.ReactElement
      },
      {
        path: PAGE.ACCOUNTING_CENTER.children.EDIT,
        element: React.createElement(ACEditPage) as React.ReactElement
      },
      {
        path: PAGE.ACCOUNTING_CENTER.children.DETAIL,
        element: React.createElement(ACDetailPage) as React.ReactElement
      },
      {
        path: PAGE.ACCOUNTING_CENTER.children.ADJUST,
        element: React.createElement(ACAdjustPage) as React.ReactElement
      }
    ]
  },
  // Expense category routes
  {
    path: PAGE.EXPENSE_CATEGORY.path,
    element: React.createElement(PrivateRoute, {
      children: React.createElement(ExpenseCategoryOutlet) as React.ReactElement,
      redirectTo: '/login'
    }) as React.ReactElement,
    children: [
      {
        index: true,
        element: React.createElement(ExpenseCategoryPage) as React.ReactElement
      },
      {
        path: PAGE.EXPENSE_CATEGORY.children.ADD,
        element: React.createElement(ECAddPage) as React.ReactElement
      },
      {
        path: PAGE.EXPENSE_CATEGORY.children.DETAIL,
        element: React.createElement(ECDetailPage) as React.ReactElement
      },
      {
        path: PAGE.EXPENSE_CATEGORY.children.EDIT,
        element: React.createElement(ECEditPage) as React.ReactElement
      },
      {
        path: PAGE.EXPENSE_CATEGORY.children.ADJUST,
        element: React.createElement(ECAdjustPage) as React.ReactElement
      }
    ]
  },
  // Revenue category routes
  {
    path: PAGE.REVENUE_CATEGORY.path,
    element: React.createElement(PrivateRoute, {
      children: React.createElement(RevenueCategoryOutlet) as React.ReactElement,
      redirectTo: '/login'
    }) as React.ReactElement,
    children: [
      {
        index: true,
        element: React.createElement(RevenueCategoryPage) as React.ReactElement
      },
      {
        path: PAGE.REVENUE_CATEGORY.children.ADD,
        element: React.createElement(RCAddPage) as React.ReactElement
      },
      {
        path: PAGE.REVENUE_CATEGORY.children.DETAIL,
        element: React.createElement(RCDetailPage) as React.ReactElement
      },
      {
        path: PAGE.REVENUE_CATEGORY.children.EDIT,
        element: React.createElement(RCEditPage) as React.ReactElement
      },
      {
        path: PAGE.REVENUE_CATEGORY.children.ADJUST,
        element: React.createElement(RCAdjustPage) as React.ReactElement
      }
    ]
  },
  {
    path: PAGE.ADMIN_DASHBOARD.path,
    element: React.createElement(AdminRoute, {
      children: React.createElement(AdminDashboardPage) as React.ReactElement,
      redirectTo: '/dashboard'
    }) as React.ReactElement
  },
  {
    path: PAGE.ADMIN_SUBSCRIBERS.path,
    element: React.createElement(AdminRoute, {
      children: React.createElement(AdminSubscribersPage) as React.ReactElement,
      redirectTo: '/dashboard'
    }) as React.ReactElement
  },
  {
    path: PAGE.ADMIN_SUBSCRIBERS.path + '/:subscriberId',
    element: React.createElement(AdminRoute, {
      children: React.createElement(SubscriberPage) as React.ReactElement,
      redirectTo: '/dashboard'
    }) as React.ReactElement
  },
  // 404 route
  {
    path: '*',
    element: React.createElement(NotFoundPage) as React.ReactElement
  }
]);

export default routes;
